import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  WhatsApp as WhatsAppIcon,
  RoomOutlined as RoomOutlinedIcon,
  EmailOutlined as EmailOutlinedIcon,
  LocalOfferOutlined as LocalOfferOutlinedIcon,
  ZoomOutMap as ZoomOutMapIcon
} from "@material-ui/icons";
import { openApi } from "../../services/api";

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(180deg, #dde7ff 0%, #edf3ff 22%, #f6f8ff 48%, #eef3fb 76%, #e7eef8 100%)",
    color: "#10213a",
    paddingBottom: theme.spacing(7)
  },
  hero: {
    padding: theme.spacing(5, 0, 2.5),
    background:
      "radial-gradient(circle at top left, rgba(37, 99, 235, 0.2), transparent 34%), radial-gradient(circle at top right, rgba(109, 40, 217, 0.14), transparent 28%)"
  },
  mainContainer: {
    maxWidth: 1180,
    width: "100%",
    margin: "0 auto",
    padding: theme.spacing(0, 2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 1.25)
    }
  },
  heroCard: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 32,
    padding: theme.spacing(4),
    background: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,249,255,0.98) 100%)",
    border: "1px solid rgba(30, 58, 138, 0.1)",
    boxShadow: "0 22px 52px rgba(18, 36, 84, 0.12)",
    color: "#10213a",
    "&:before": {
      content: "\"\"",
      position: "absolute",
      top: -110,
      right: -50,
      width: 300,
      height: 300,
      borderRadius: "50%",
      background: "radial-gradient(circle, rgba(67, 56, 202, 0.14) 0%, rgba(67, 56, 202, 0) 72%)"
    },
    "&:after": {
      content: "\"\"",
      position: "absolute",
      bottom: -70,
      left: -40,
      width: 240,
      height: 240,
      borderRadius: "50%",
      background: "radial-gradient(circle, rgba(37, 99, 235, 0.12) 0%, rgba(37, 99, 235, 0) 72%)"
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2.75)
    }
  },
  heroContent: {
    position: "relative",
    zIndex: 1
  },
  heroEyebrow: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: theme.spacing(0.65, 1.25),
    marginBottom: theme.spacing(1.75),
    background: "rgba(67, 56, 202, 0.08)",
    border: "1px solid rgba(67, 56, 202, 0.12)",
    color: "#4338ca",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    fontSize: "0.66rem"
  },
  heroIdentity: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      textAlign: "center"
    }
  },
  logo: {
    width: 120,
    height: 120,
    borderRadius: 30,
    objectFit: "cover",
    background: "#f3f4f6",
    border: "1px solid rgba(67, 56, 202, 0.12)",
    boxShadow: "0 14px 28px rgba(18, 36, 84, 0.16)"
  },
  heroTitle: {
    fontWeight: 800,
    letterSpacing: "-0.03em",
    lineHeight: 1.08,
    color: "#111827",
    fontSize: "2.35rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "2rem"
    }
  },
  heroSubtitle: {
    color: "rgba(16,33,58,0.78)",
    marginTop: theme.spacing(1.2),
    maxWidth: 620
  },
  heroHighlights: {
    marginTop: theme.spacing(1.5),
    color: "rgba(16,33,58,0.62)",
    fontWeight: 700,
    fontSize: "0.92rem",
    [theme.breakpoints.down("sm")]: {
      textAlign: "center"
    }
  },
  heroDetails: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center"
    }
  },
  infoLine: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: "rgba(16,33,58,0.72)"
  },
  heroSideCard: {
    position: "relative",
    zIndex: 1,
    borderRadius: 24,
    padding: theme.spacing(2.25),
    background: "linear-gradient(180deg, rgba(246,249,255,0.96) 0%, rgba(239,244,255,0.98) 100%)",
    border: "1px solid rgba(37, 99, 235, 0.12)",
    boxShadow: "0 16px 34px rgba(18, 36, 84, 0.08)",
    width: "100%",
    boxSizing: "border-box"
  },
  heroSideStack: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1.25),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))"
    },
    [theme.breakpoints.down(420)]: {
      gridTemplateColumns: "1fr"
    }
  },
  heroStatCard: {
    display: "grid",
    gridTemplateColumns: "42px 1fr",
    alignItems: "center",
    gap: theme.spacing(1.1),
    minHeight: 88,
    padding: theme.spacing(1.35, 1.3),
    borderRadius: 18,
    background: "#fff",
    border: "1px solid rgba(37, 99, 235, 0.08)"
  },
  heroStatIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, rgba(37, 99, 235, 0.12) 0%, rgba(67, 56, 202, 0.12) 100%)",
    color: "#2f4eb7"
  },
  heroMetaLabel: {
    color: "rgba(16,33,58,0.6)",
    fontWeight: 700,
    fontSize: "0.8rem",
    lineHeight: 1.1
  },
  heroMetaValue: {
    color: "#10213a",
    fontWeight: 900,
    fontSize: "1.6rem",
    lineHeight: 1.1,
    marginTop: theme.spacing(0.25)
  },
  heroMetaCaption: {
    color: "rgba(16,33,58,0.56)",
    fontWeight: 700,
    fontSize: "0.82rem",
    lineHeight: 1.1,
    marginTop: theme.spacing(0.2)
  },
  heroHelperText: {
    color: "rgba(16,33,58,0.58)",
    fontWeight: 600
  },
  heroPrimaryButton: {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    borderRadius: 16,
    padding: "0 18px",
    minHeight: 50,
    textTransform: "none",
    fontSize: "0.95rem",
    fontWeight: 800,
    lineHeight: 1.2,
    background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
    color: "#fff",
    boxShadow: "0 14px 28px rgba(12, 82, 38, 0.24)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    overflow: "hidden",
    whiteSpace: "nowrap",
    textAlign: "center",
    "& .MuiButton-startIcon": {
      margin: 0,
      flexShrink: 0
    },
    "& .MuiButton-label": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(1),
      width: "100%",
      minWidth: 0,
      overflow: "hidden"
    },
    "&:hover": {
      background: "linear-gradient(135deg, #15803d 0%, #166534 100%)"
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      minHeight: 50,
      whiteSpace: "normal"
    }
  },
  heroPrimaryButtonText: {
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  filterWrap: {
    marginTop: theme.spacing(2.25),
    borderRadius: 24,
    padding: theme.spacing(2),
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(16,33,58,0.08)",
    boxShadow: "0 14px 32px rgba(16,33,58,0.08)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.6)
    }
  },
  searchHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.25),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch"
    }
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 18,
      background: "#fff",
      minHeight: 56,
      boxShadow: "0 10px 22px rgba(15, 23, 42, 0.06)",
      "& fieldset": {
        borderColor: "rgba(37, 99, 235, 0.14)"
      },
      "&:hover fieldset": {
        borderColor: "rgba(37, 99, 235, 0.28)"
      },
      "&.Mui-focused fieldset": {
        borderColor: "#2563eb"
      }
    }
  },
  categoryRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  categoryChip: {
    borderRadius: 999,
    fontWeight: 700,
    height: 38,
    background: "#f5f7fb",
    border: "1px solid rgba(16,33,58,0.08)",
    color: "#42526b",
    transition: "all 180ms ease",
    "&:hover": {
      background: "#eef3ff",
      borderColor: "rgba(37, 99, 235, 0.24)"
    }
  },
  activeCategoryChip: {
    background: "linear-gradient(135deg, #1d4ed8 0%, #4338ca 100%) !important",
    color: "#fff !important",
    borderColor: "transparent !important",
    boxShadow: "0 10px 22px rgba(37, 99, 235, 0.18)"
  },
  statsText: {
    color: "rgba(16,33,58,0.64)",
    fontWeight: 600
  },
  resultCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 999,
    padding: theme.spacing(0.75, 1.4),
    background: "#e8efff",
    color: "#28457a",
    fontWeight: 700
  },
  productSection: {
    paddingTop: theme.spacing(3)
  },
  gridContainer: {
    justifyContent: "center"
  },
  productGridItem: {
    display: "flex",
    justifyContent: "center"
  },
  productCard: {
    width: "100%",
    maxWidth: 340,
    minWidth: 280,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 26,
    overflow: "hidden",
    background: "#fff",
    border: "1px solid rgba(16,33,58,0.1)",
    boxShadow: "0 18px 40px rgba(16,33,58,0.1)",
    transition: "transform 180ms ease, box-shadow 180ms ease",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 24px 52px rgba(16,33,58,0.15)"
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      minWidth: 0
    }
  },
  mediaWrap: {
    position: "relative",
    background: "linear-gradient(180deg, #dfe9ff 0%, #f5f8ff 100%)",
    cursor: "pointer"
  },
  productImage: {
    height: 230,
    objectFit: "cover"
  },
  imageHint: {
    position: "absolute",
    right: theme.spacing(1.25),
    bottom: theme.spacing(1.25),
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.55, 1),
    borderRadius: 999,
    background: "rgba(15, 23, 42, 0.62)",
    color: "#fff",
    fontSize: "0.76rem",
    fontWeight: 700,
    backdropFilter: "blur(8px)"
  },
  productContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    flex: 1,
    padding: theme.spacing(2.2)
  },
  productCategory: {
    width: "fit-content",
    background: "rgba(67, 56, 202, 0.08)",
    color: "#4338ca",
    fontWeight: 700
  },
  productTitle: {
    fontWeight: 800,
    color: "#10213a"
  },
  productDescription: {
    color: "rgba(16,33,58,0.7)",
    minHeight: 42,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden"
  },
  productFooter: {
    marginTop: "auto"
  },
  priceWrap: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.25)
  },
  priceLabel: {
    color: "rgba(16,33,58,0.52)",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontSize: "0.72rem"
  },
  oldPrice: {
    color: "rgba(16,33,58,0.42)",
    textDecoration: "line-through",
    fontWeight: 700,
    minHeight: 20
  },
  price: {
    fontWeight: 900,
    color: "#0f766e",
    fontSize: "1.35rem",
    lineHeight: 1.1
  },
  whatsappButton: {
    borderRadius: 14,
    padding: theme.spacing(1.2, 2),
    fontWeight: 800,
    textTransform: "none",
    background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #15803d 0%, #166534 100%)"
    }
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
    borderRadius: 24,
    background: "rgba(255,255,255,0.94)",
    border: "1px solid rgba(16,33,58,0.1)",
    boxShadow: "0 18px 38px rgba(16,33,58,0.08)"
  },
  emptyStateAction: {
    marginTop: theme.spacing(2)
  },
  loadingWrap: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#edf3ff"
  },
  lightboxPaper: {
    backgroundColor: "transparent",
    boxShadow: "none",
    overflow: "hidden"
  },
  lightboxBackdrop: {
    backgroundColor: "rgba(4, 12, 32, 0.88)"
  },
  lightboxBody: {
    position: "relative",
    width: "min(92vw, 1100px)",
    height: "min(86vh, 860px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto"
  },
  lightboxImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    borderRadius: 22,
    boxShadow: "0 24px 60px rgba(0, 0, 0, 0.34)"
  },
  lightboxClose: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 2,
    background: "rgba(255,255,255,0.12)",
    color: "#fff",
    "&:hover": {
      background: "rgba(255,255,255,0.22)"
    }
  }
}));

const formatCurrency = (value) => {
  const numericValue = Number(value || 0);
  return numericValue.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
};

const normalizePhone = (value) => (value || "").replace(/\D/g, "");

const formatResultCount = (count) => `${count} ${count === 1 ? "produto encontrado" : "produtos encontrados"}`;

const formatHeroStatCaption = (count, singular, plural) => (count === 1 ? singular : plural);

const PublicCatalog = () => {
  const classes = useStyles();
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [catalogCategories, setCatalogCategories] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({
    companyName: "",
    companyPhone: "",
    companyEmail: "",
    companyAddress: "",
    companyLogo: ""
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [lightboxProduct, setLightboxProduct] = useState(null);

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const { data } = await openApi.get(`/public-catalog/${slug}`);
        setProducts(Array.isArray(data.products) ? data.products : []);
        setCatalogCategories(Array.isArray(data.categories) ? data.categories : []);
        setCompanyInfo({
          companyName: data.companyName || "",
          companyPhone: data.companyPhone || "",
          companyEmail: data.companyEmail || "",
          companyAddress: data.companyAddress || "",
          companyLogo: data.companyLogo || ""
        });
      } catch (err) {
        console.error(err);
        setCompanyInfo({
          companyName: "",
          companyPhone: "",
          companyEmail: "",
          companyAddress: "",
          companyLogo: ""
        });
        setCatalogCategories([]);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCatalog();
  }, [slug]);

  const categories = useMemo(() => {
    return Array.from(
      new Set(
        [...catalogCategories, ...products
          .map((product) => (product.category || "").trim())
          .filter(Boolean)]
      )
    ).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [catalogCategories, products]);

  const filteredProducts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return products.filter((product) => {
      const matchesCategory =
        selectedCategory === "all" ||
        (product.category || "").trim() === selectedCategory;

      const matchesSearch =
        !normalizedSearch ||
        (product.name || "").toLowerCase().includes(normalizedSearch) ||
        (product.description || "").toLowerCase().includes(normalizedSearch);

      return matchesCategory && matchesSearch;
    });
  }, [products, searchTerm, selectedCategory]);

  const companyPhone = normalizePhone(companyInfo.companyPhone);

  const handleMissingPhone = () => {
    toast.error("Este catálogo ainda não possui um telefone de WhatsApp configurado.");
  };

  const openCompanyWhatsApp = () => {
    if (!companyPhone) {
      handleMissingPhone();
      return;
    }

    window.open(`https://wa.me/${companyPhone}`, "_blank", "noopener,noreferrer");
  };

  const openWhatsAppForProduct = (product) => {
    if (!companyPhone) {
      handleMissingPhone();
      return;
    }

    const messageLines = [
      `Olá! Tenho interesse no produto "${product.name}".`,
      `Valor: ${formatCurrency(product.price)}.`
    ];

    if (companyInfo.companyName) {
      messageLines.push(`Catálogo: ${companyInfo.companyName}.`);
    }

    messageLines.push(`Link: ${window.location.href}`);

    const url = `https://wa.me/${companyPhone}?text=${encodeURIComponent(messageLines.join(" "))}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div className={classes.loadingWrap}>
        <CircularProgress color="primary" />
      </div>
    );
  }

  if (!companyInfo.companyName) {
    return (
      <div className={classes.page}>
        <Container maxWidth="md" style={{ paddingTop: 80 }}>
          <Paper className={classes.emptyState}>
            <Typography variant="h4" gutterBottom style={{ fontWeight: 800 }}>
              Catálogo indisponível
            </Typography>
            <Typography variant="body1" color="textSecondary">
              O catálogo público não foi encontrado ou ainda não está configurado.
            </Typography>
          </Paper>
        </Container>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      <div className={classes.hero}>
        <Container maxWidth={false} className={classes.mainContainer}>
          <Paper className={classes.heroCard}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <div className={classes.heroContent}>
                  <div className={classes.heroEyebrow}>Catálogo público</div>

                  <div className={classes.heroIdentity}>
                    {companyInfo.companyLogo ? (
                      <img
                        src={companyInfo.companyLogo}
                        alt={companyInfo.companyName}
                        className={classes.logo}
                      />
                    ) : null}

                    <div>
                      <Typography variant="h3" className={classes.heroTitle}>
                        {companyInfo.companyName}
                      </Typography>
                      <Typography variant="body1" className={classes.heroSubtitle}>
                        Escolha um produto, veja os detalhes e envie seu pedido direto pelo WhatsApp.
                      </Typography>
                    </div>
                  </div>

                  <Typography variant="body2" className={classes.heroHighlights}>
                    Atendimento rápido • Orçamento pelo WhatsApp • Produtos personalizados
                  </Typography>

                  <div className={classes.heroDetails}>
                    {companyInfo.companyAddress ? (
                      <div className={classes.infoLine}>
                        <RoomOutlinedIcon fontSize="small" />
                        <Typography variant="body2">{companyInfo.companyAddress}</Typography>
                      </div>
                    ) : null}

                    {companyInfo.companyEmail ? (
                      <div className={classes.infoLine}>
                        <EmailOutlinedIcon fontSize="small" />
                        <Typography variant="body2">{companyInfo.companyEmail}</Typography>
                      </div>
                    ) : null}
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} md={4}>
                <div className={classes.heroSideCard}>
                  <div className={classes.heroSideStack}>
                    <div className={classes.heroStatCard}>
                      <div className={classes.heroStatIcon}>
                        <SearchIcon fontSize="small" />
                      </div>
                      <div>
                        <Typography variant="body2" className={classes.heroMetaLabel}>
                          Produtos
                        </Typography>
                        <Typography className={classes.heroMetaValue}>
                          {filteredProducts.length}
                        </Typography>
                        <Typography className={classes.heroMetaCaption}>
                          {formatHeroStatCaption(filteredProducts.length, "encontrado", "encontrados")}
                        </Typography>
                      </div>
                    </div>

                    <div className={classes.heroStatCard}>
                      <div className={classes.heroStatIcon}>
                        <LocalOfferOutlinedIcon fontSize="small" />
                      </div>
                      <div>
                        <Typography variant="body2" className={classes.heroMetaLabel}>
                          Categorias
                        </Typography>
                        <Typography className={classes.heroMetaValue}>
                          {categories.length}
                        </Typography>
                        <Typography className={classes.heroMetaCaption}>
                          {formatHeroStatCaption(categories.length, "disponível", "disponíveis")}
                        </Typography>
                      </div>
                    </div>
                  </div>

                  <Box mt={2.25}>
                    <Button
                      fullWidth
                      onClick={openCompanyWhatsApp}
                      className={classes.heroPrimaryButton}
                      startIcon={<WhatsAppIcon />}
                    >
                      <span className={classes.heroPrimaryButtonText}>
                        Chamar no WhatsApp
                      </span>
                    </Button>
                  </Box>

                  {!companyPhone ? (
                    <Box mt={1.25}>
                      <Typography variant="caption" className={classes.heroHelperText}>
                        WhatsApp indisponível no momento. Configure o telefone do catálogo.
                      </Typography>
                    </Box>
                  ) : null}
                </div>
              </Grid>
            </Grid>
          </Paper>

          <Paper className={classes.filterWrap}>
            <div className={classes.searchHeader}>
              <Typography variant="body2" className={classes.statsText}>
                Explore os produtos e refine por categoria.
              </Typography>
              <div className={classes.resultCount}>
                {formatResultCount(filteredProducts.length)}
              </div>
            </div>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar produto, categoria ou palavra-chave..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={classes.searchField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="primary" />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <div className={classes.categoryRow}>
                  <Chip
                    clickable
                    label="Todas"
                    onClick={() => setSelectedCategory("all")}
                    className={`${classes.categoryChip} ${selectedCategory === "all" ? classes.activeCategoryChip : ""}`}
                    icon={<LocalOfferOutlinedIcon />}
                  />

                  {categories.map((category) => (
                    <Chip
                      key={category}
                      clickable
                      label={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`${classes.categoryChip} ${selectedCategory === category ? classes.activeCategoryChip : ""}`}
                    />
                  ))}
                </div>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </div>

      <Container maxWidth={false} className={classes.mainContainer}>
        <div className={classes.productSection}>
          {filteredProducts.length === 0 ? (
            <Paper className={classes.emptyState}>
              <Typography variant="h5" gutterBottom style={{ fontWeight: 800 }}>
                Nenhum produto encontrado
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Nenhum produto encontrado. Tente buscar por outro termo ou fale conosco pelo WhatsApp.
              </Typography>
              <Box className={classes.emptyStateAction}>
                <Button
                  onClick={openCompanyWhatsApp}
                  className={classes.whatsappButton}
                  startIcon={<WhatsAppIcon />}
                >
                  Falar no WhatsApp
                </Button>
              </Box>
            </Paper>
          ) : (
            <Grid container spacing={3} className={classes.gridContainer}>
              {filteredProducts.map((product) => (
                <Grid item xs={12} sm={6} md={4} lg={4} key={product.id} className={classes.productGridItem}>
                  <Card className={classes.productCard}>
                    <div
                      className={classes.mediaWrap}
                      onClick={() => product.imageUrl && setLightboxProduct(product)}
                      role={product.imageUrl ? "button" : undefined}
                      tabIndex={product.imageUrl ? 0 : undefined}
                      onKeyDown={(event) => {
                        if (product.imageUrl && (event.key === "Enter" || event.key === " ")) {
                          event.preventDefault();
                          setLightboxProduct(product);
                        }
                      }}
                    >
                      {product.imageUrl ? (
                        <>
                          <CardMedia
                            component="img"
                            image={product.imageUrl}
                            title={product.name}
                            className={classes.productImage}
                          />
                          <div className={classes.imageHint}>
                            <ZoomOutMapIcon fontSize="inherit" />
                            Ver imagem
                          </div>
                        </>
                      ) : (
                        <Box
                          height={230}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          style={{ color: "rgba(16,33,58,0.45)" }}
                        >
                          <Typography variant="body2">Sem imagem</Typography>
                        </Box>
                      )}
                    </div>

                    <CardContent className={classes.productContent}>
                      {product.category ? (
                        <Chip
                          size="small"
                          label={product.category}
                          className={classes.productCategory}
                        />
                      ) : null}

                      <Typography variant="h6" className={classes.productTitle}>
                        {product.name}
                      </Typography>

                      <Typography variant="body2" className={classes.productDescription}>
                        {product.description || "Sem descrição cadastrada."}
                      </Typography>

                      {product.shippingInfo ? (
                        <Typography variant="caption" style={{ color: "rgba(16,33,58,0.68)", fontWeight: 600 }}>
                          {product.shippingInfo}
                        </Typography>
                      ) : null}

                      <div className={classes.productFooter}>
                        <div className={classes.priceWrap}>
                          <Typography className={classes.priceLabel}>
                            Investimento
                          </Typography>
                          <Typography variant="body2" className={classes.oldPrice}>
                            &nbsp;
                          </Typography>
                          <Typography className={classes.price}>
                            {formatCurrency(product.price)}
                          </Typography>
                        </div>

                        <Box mt={2}>
                          <Button
                            fullWidth
                            onClick={() => openWhatsAppForProduct(product)}
                            className={classes.whatsappButton}
                            startIcon={<WhatsAppIcon />}
                          >
                            Enviar no WhatsApp
                          </Button>
                        </Box>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </div>
      </Container>

      <Dialog
        open={Boolean(lightboxProduct)}
        onClose={() => setLightboxProduct(null)}
        maxWidth={false}
        BackdropProps={{ className: classes.lightboxBackdrop }}
        PaperProps={{ className: classes.lightboxPaper }}
      >
        <div className={classes.lightboxBody}>
          <IconButton
            className={classes.lightboxClose}
            onClick={() => setLightboxProduct(null)}
            aria-label="Fechar imagem"
          >
            <CloseIcon />
          </IconButton>

          {lightboxProduct?.imageUrl ? (
            <img
              src={lightboxProduct.imageUrl}
              alt={lightboxProduct.name}
              className={classes.lightboxImage}
            />
          ) : null}
        </div>
      </Dialog>
    </div>
  );
};

export default PublicCatalog;
